// PATH: src/api/payments.ts — accès au module paiements (/api/v1/school).
// Réservé à la direction côté backend : un 403 est une réponse attendue, pas
// une anomalie (cf. PaymentsPage).
import { useMemo } from "react";
import { useApiClient } from "./apiClients";

const BASE = "/api/v1/school";

/** Rythme de facturation d'une école. Distinct des trimestres du bulletin. */
export type PeriodKind = "MOIS" | "TRIMESTRE" | "ANNEE" | "LIBRE";

export interface PaymentPeriod {
  id: number;
  school_year_id: number;
  label: string;
  kind: PeriodKind;
  sort_order: number;
  start_date?: string;
  end_date?: string;
  year_label?: string;
}

/** Tarif d'une classe pour une période. */
export interface ClassFee {
  class_id: number;
  period_id: number;
  amount: number;
  class_label?: string;
}

export interface PaymentRow {
  student_id: number;
  student_name: string;
  status: "" | "PAYE" | "IMPAYE";
  reminder_count: number;
  last_reminder_at?: string;
  /** Montant figé au pointage. Absent tant que l'élève n'est pas pointé. */
  amount_due?: number;
  /** Tarif actuel de la classe de l'élève. Absent si pas de classe/tarif. */
  amount_current?: number;
  /** Absent si l'élève n'est affecté à aucune classe. */
  class_label?: string;
  /** Inscription de l'élève sur l'année de la période — ouvre le reçu. */
  enrollment_id?: number;
  /** Renseignés par l'historique d'un élève (listByStudent), où la ligne EST
   *  une période. La liste par période ne les remplit pas : l'appelant connaît
   *  déjà la période qu'il demande. */
  period_id?: number;
  period_label?: string;
}

export const usePaymentsApi = () => {
  const http = useApiClient("tilmischool");

  return useMemo(
    () => ({
      // ── Périodes ──
      listPeriods: (schoolYearId?: number) =>
        http
          .get<PaymentPeriod[]>(`${BASE}/payment-periods`, { params: { school_year_id: schoolYearId } })
          .then((r) => r.data ?? []),
      createPeriod: (p: Partial<PaymentPeriod>) =>
        http.post<PaymentPeriod>(`${BASE}/payment-periods`, p).then((r) => r.data),
      updatePeriod: (id: number, p: Partial<PaymentPeriod>) => http.put(`${BASE}/payment-periods/${id}`, p),
      deletePeriod: (id: number) => http.delete(`${BASE}/payment-periods/${id}`),

      // ── Tarifs par classe ──
      listFees: (periodId: number) =>
        http.get<ClassFee[]>(`${BASE}/payment-periods/${periodId}/fees`).then((r) => r.data ?? []),
      setFee: (periodId: number, classId: number, amount: number) =>
        http.put(`${BASE}/payment-periods/${periodId}/fees`, { class_id: classId, amount }),

      // ── Statuts ──
      list: (periodId: number) =>
        http.get<PaymentRow[]>(`${BASE}/payments`, { params: { period_id: periodId } }).then((r) => r.data ?? []),
      // Historique d'un élève : toutes les périodes des années où il est inscrit.
      // period_label est renseigné ici (il nomme chaque ligne), contrairement à
      // la liste par période.
      listByStudent: (studentId: number) =>
        http.get<PaymentRow[]>(`${BASE}/students/${studentId}/payments`).then((r) => r.data ?? []),
      setStatus: (studentId: number, periodId: number, status: "PAYE" | "IMPAYE") =>
        http.put(`${BASE}/payments`, { student_id: studentId, period_id: periodId, status }),
      runReminders: (periodId?: number) =>
        http.post<{ sent: number }>(`${BASE}/payments/reminders`, { period_id: periodId }).then((r) => r.data),
    }),
    [http]
  );
};
